import { DocxImportError, toDocxError } from '../errors';
import { DEFAULT_LIMITS, conversionTimeoutMs, WATCHDOG_MS } from '../limits';

/*
 * Runs a conversion in a fresh Web Worker (terminated afterwards: terminate()
 * is the only reliable way to stop synchronous parsing). Falls back to the
 * main thread, with a stricter size limit, when workers are unavailable.
 */

let nextJobId = 1;

const defaultCreateWorker = async () => {
    if (typeof Worker === 'undefined') throw new DocxImportError('WORKER_UNAVAILABLE');
    const mod = await import('./createDocxWorker');
    return mod.default();
};

/** Normalises a result so asset bytes are Uint8Arrays, whichever path produced it. */
function normalize(result) {
    return {
        html: result.html,
        assets: result.assets.map((a) => ({ ...a, bytes: a.bytes instanceof Uint8Array ? a.bytes : new Uint8Array(a.bytes) })),
        warnings: result.warnings || [],
        stats: result.stats || {},
    };
}

async function runOnMainThread(bytes, options, limits) {
    if (bytes.byteLength > limits.maxInputBytesMainThread) throw new DocxImportError('LIMIT_INPUT');
    const { convertDocx } = await import('../core/convert');
    return normalize(convertDocx(new Uint8Array(bytes), options));
}

/**
 * @param {ArrayBuffer} bytes the .docx contents (transferred to the worker; don't reuse it)
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @param {(phase: string) => void} [opts.onProgress]
 * @param {object} [opts.options] converter options (limits, idPrefix, tabString)
 * @param {() => Worker|Promise<Worker>} [opts.createWorker] injectable for tests
 * @param {number} [opts.timeoutMs] hard limit (default: by size)
 * @param {number} [opts.watchdogMs] max time without progress
 */
export async function runConversion(bytes, opts = {}) {
    const options = opts.options || {};
    const limits = { ...DEFAULT_LIMITS, ...(options.limits || {}) };
    if (opts.signal && opts.signal.aborted) throw new DocxImportError('ABORTED');

    let worker;
    try {
        worker = await (opts.createWorker || defaultCreateWorker)();
    } catch (err) {
        return runOnMainThread(bytes, options, limits);
    }

    const jobId = nextJobId++;
    const timeoutMs = opts.timeoutMs || conversionTimeoutMs(bytes.byteLength);
    const watchdogMs = opts.watchdogMs || WATCHDOG_MS;
    const inputCopy = bytes.byteLength <= limits.maxInputBytesMainThread ? bytes.slice(0) : null;

    return new Promise((resolve, reject) => {
        let settled = false;
        let heardBack = false;
        let watchdog = null;

        const cleanup = () => {
            settled = true;
            clearTimeout(hardTimer);
            clearTimeout(watchdog);
            if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
            try {
                worker.terminate();
            } catch (e) {
                // already gone
            }
        };
        const fail = (err) => {
            if (settled) return;
            cleanup();
            reject(err);
        };
        const armWatchdog = () => {
            clearTimeout(watchdog);
            watchdog = setTimeout(() => fail(new DocxImportError('TIMEOUT', 'no progress')), watchdogMs);
        };
        const onAbort = () => fail(new DocxImportError('ABORTED'));
        const hardTimer = setTimeout(() => fail(new DocxImportError('TIMEOUT')), timeoutMs);

        if (opts.signal) opts.signal.addEventListener('abort', onAbort);
        armWatchdog();

        worker.onmessage = (event) => {
            const msg = event.data;
            if (settled || !msg || msg.v !== 1 || msg.jobId !== jobId) return;
            heardBack = true;
            if (msg.type === 'progress') {
                armWatchdog();
                if (opts.onProgress) opts.onProgress(msg.phase);
            } else if (msg.type === 'result') {
                cleanup();
                resolve(normalize(msg));
            } else if (msg.type === 'error') {
                fail(new DocxImportError(msg.code, msg.detail));
            }
        };
        worker.onerror = (event) => {
            if (event && event.preventDefault) event.preventDefault();
            if (!heardBack && inputCopy && !settled) {
                // The worker never started (e.g. its chunk failed to load): convert here instead.
                cleanup();
                runOnMainThread(inputCopy, options, limits).then(resolve, (err) => reject(toDocxError(err)));
                return;
            }
            fail(new DocxImportError('INTERNAL', event && event.message));
        };

        try {
            worker.postMessage({ v: 1, type: 'convert', jobId, bytes, options }, [bytes]);
        } catch (err) {
            fail(toDocxError(err));
        }
    });
}
